/**
 * Web Vitals reporter — dev console + optional analytics hook.
 *
 * Why this matters:
 *  - LCP (largest contentful paint): how fast the hero feels.
 *  - CLS (cumulative layout shift): jumpy layout hurts trust.
 *  - INP (interaction to next paint): how snappy taps/clicks feel.
 *  - FCP/TTFB: supporting metrics.
 *
 * In production, we forward to PostHog (already on the page) so we can see
 * real-user numbers over time, not just synthetic Lighthouse runs.
 */
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";


function scoreLabel(metric) {
  // Google's thresholds. Returns "good" | "ni" (needs improvement) | "poor".
  const { name, value } = metric;
  const thresholds = {
    LCP:  [2500, 4000],
    CLS:  [0.1, 0.25],
    INP:  [200, 500],
    FCP:  [1800, 3000],
    TTFB: [800, 1800],
  }[name];
  if (!thresholds) return "unknown";
  return value <= thresholds[0] ? "good" : value <= thresholds[1] ? "ni" : "poor";
}


function report(metric) {
  const rating = scoreLabel(metric);
  const rounded = metric.name === "CLS"
    ? metric.value.toFixed(3)
    : Math.round(metric.value);

  if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log(`[vitals] ${metric.name} = ${rounded} (${rating})`);
  }

  // Forward to PostHog if present on the page (loaded via index.html).
  try {
    if (typeof window !== "undefined" && window.posthog?.capture) {
      window.posthog.capture("web_vitals", {
        metric: metric.name,
        value: metric.value,
        rating,
        path: window.location.pathname,
      });
    }
  } catch (e) {
    // PostHog might not be loaded / blocked by adblock — non-fatal.
    // eslint-disable-next-line no-console
    console.debug("[vitals] posthog forward failed", e?.message || e);
  }
}


let _registered = false;

export function initWebVitals() {
  if (_registered) return;
  _registered = true;
  try {
    onCLS(report);
    onFCP(report);
    onINP(report);
    onLCP(report);
    onTTFB(report);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[vitals] failed to init", e);
  }
}
